import { AfterViewInit, Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BaseType } from '../../../models/item-data-models';
import { ItemDataService } from '../../../services/item-data.service';
import { ItemLabelComponent } from '../../../common/item-label/item-label.component';
import { poe2 } from '../../../../game';
import { FilterTitleComponent } from '../../../common/filter-title/filter-title.component';

@Component({
  selector: 'app-bulk-import-gems-dialog',
  imports: [FormsModule, ItemLabelComponent, FilterTitleComponent],
  templateUrl: './bulk-import-gems-dialog.component.html',
  styleUrls: ['../../../common/dialogs.scss', './bulk-import-gems-dialog.component.scss']
})
export class BulkImportGemsDialogComponent implements AfterViewInit {
  @Output() close = new EventEmitter<void>();
  @Output() import = new EventEmitter<BaseType[]>();
  @ViewChild('importTextElement') importTextRef!: ElementRef;

  importText = '';
  selectedGems: BaseType[] = [];
  private parseSubject = new Subject<void>();

  gemTypes: BaseType[] = [];
  private gemNameMap = new Map<string, BaseType>();
  private gemRegex: RegExp | null = null;

  constructor(itemDataService: ItemDataService) {
    const itemData = itemDataService.getItemData();
    this.gemTypes = poe2 ? [] : itemData.find(i => i.itemType === 'Skill Gems')!.baseTypes.filter(b => !itemDataService.getTransfiguredGems().includes(b.name))
      .concat(itemData.find(i => i.itemType === 'Support Gems')!.baseTypes);

    // Create a map for efficient lookup and build regex pattern
    this.buildGemLookupStructures();

    // Set up debounced parsing
    this.parseSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.doParse();
    });
  }

  private buildGemLookupStructures() {
    // Create map for efficient lookup with both full names and shortened support gem names
    this.gemTypes.forEach(gem => {
      this.gemNameMap.set(gem.name.toLowerCase(), gem);

      // For support gems, also add the name without "Support"
      if (gem.name.toLowerCase().endsWith(' support')) {
        const shortName = gem.name.toLowerCase().replace(' support', '');
        this.gemNameMap.set(shortName, gem);

        // Special handling for "Added X Damage Support" pattern
        const addedDamagePattern = /^added (.+) damage support$/i;
        const match = gem.name.match(addedDamagePattern);
        if (match) {
          const element = match[1].toLowerCase();
          // Add variations: "added X", "X damage"
          this.gemNameMap.set(`added ${element}`, gem);
          this.gemNameMap.set(`${element} damage`, gem);
        }
      }
    });

    // Create regex patterns including both full names and all variations
    const allPatterns: string[] = [];

    this.gemTypes.forEach(gem => {
      // Add the full name
      allPatterns.push(gem.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

      // For support gems, also add the shortened name
      if (gem.name.toLowerCase().endsWith(' support')) {
        const shortName = gem.name.replace(' support', '').replace(' Support', '');
        allPatterns.push(shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

        // Special handling for "Added X Damage Support" pattern
        const addedDamagePattern = /^added (.+) damage support$/i;
        const match = gem.name.match(addedDamagePattern);
        if (match) {
          const element = match[1];
          // Add escaped variations: "added X", "X damage"
          allPatterns.push(`added ${element}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
          allPatterns.push(`${element} damage`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        }
      }
    });

    // Sort by length (longest first) to avoid partial matches - this is crucial!
    allPatterns.sort((a, b) => b.length - a.length);

    if (allPatterns.length > 0) {
      // Use word boundaries to ensure we match complete names
      this.gemRegex = new RegExp(`\\b(${allPatterns.join('|')})\\b`, 'gi');
    }
  }

  ngAfterViewInit() {
    this.importTextRef.nativeElement.focus();
  }

  parseImportText() {
    this.parseSubject.next();
  }

  private doParse() {
    this.selectedGems = [];

    if (!this.importText.trim() || !this.gemRegex) {
      return;
    }

    // Strip out color codes (^x followed by 6 hex digits, or just ^7, etc.)
    const cleanedText = this.importText.replace(/\^x[0-9A-Fa-f]{6}|\^[0-9A-Fa-f]/g, '');

    // Find all matches using the single regex on cleaned text
    const matches = cleanedText.match(this.gemRegex);

    if (matches) {
      // Use Set to track unique gem names (not match strings) to avoid duplicates
      const uniqueGems = new Set<string>();

      // Map matches back to BaseType objects
      matches.forEach(match => {
        const gem = this.gemNameMap.get(match.toLowerCase());
        if (gem && !uniqueGems.has(gem.name)) {
          uniqueGems.add(gem.name);
          this.selectedGems.push(gem);
        }
      });
    }
  }

  closeDialog() {
    this.close.emit();
  }

  importGems() {
    this.import.emit(this.selectedGems);
    this.closeDialog();
  }
}
